<template>
    <div class="p-16 bg-white rounded shadow">
        <div class="pb-4 flex items-center justify-between mb-18">
            <div class="flex items-center space-x-12">
                <svg-icon class="fill-primary-80" size="24" type="applications"/>

                <h2 class="flex items-center text-body font-semibold">
                    {{ $t('applications.title') }}
                </h2>
            </div>
        </div>

        <tabs
            :active-key="activeTab"
            :items="tabItems"
            scroll
            @set-active="activeTab = $event"
        />

        <!-- Stacked list -->
        <loading v-if="false" size="small"></loading>
        <div v-else class="pt-16 max-h-564 min-h-480 overflow-y-auto ">
            <template v-for="(item, index) in tabItems">
                <ul v-if="index === activeTab" role="list">
                    <template v-if="item.applications && item.applications.length">
                        <li v-for="application in item.applications">
                            <router-link :to="{ name: 'application', params: { id: application.id }}" class="group block">
                                <div class="flex items-center pb-4 mb-8 sm:pb-8 sm:mb-16 sm:px-0 border-b border-primary-20">
                                    <div class="min-w-0 flex-1 flex items-center gap-8">
                                        <div class="shrink-0">
                                            <avatar
                                                :image="application.image_url"
                                            >
                                                {{ getInitials(application) }}
                                            </avatar>
                                        </div>
                                        <div>
                                            <div class="flex items-center space-x-12">
                                                <p class="font-semibold text-primary truncate">{{ getFullName(application) }}</p>

                                                <span class="flex items-center gap-4">
                                                    <tooltip :content="getTooltipCreateDate(application.created_at)"
                                                             position="top"
                                                    >
                                                        <div class="flex items-center gap-4">
                                                            <svg-icon class="fill-primary-80 w-18 h-18" type="schedule" />
                                                            <span class="text-primary-80 text-body-sm">
                                                                {{ getCreateDate(application.created_at) }}
                                                            </span>
                                                        </div>
                                                    </tooltip>


                                                    <span v-if="application.time" class="flex items-center mr-12 text-body-sm text-primary-40 ml-16">
                                                        <tooltip :content="getInterviewDate(application.time)"
                                                                 position="top"
                                                        >
                                                            <svg-icon class="w-18 h-18" type="calendar-today"/>
                                                        </tooltip>
                                                    </span>
                                                </span>
                                            </div>
                                            <p class="mt-2 flex items-center gap-8 text-primary-80">
                                                {{ getJobTitle(application) }}
                                            </p>
                                        </div>
                                    </div>
                                    <div class="mr-24">
                                        <svg-icon class="w-20 h-20 text-primary-40 group-hover:text-primary" type="keyboard-arrow-right"></svg-icon>
                                    </div>
                                </div>
                            </router-link>
                        </li>
                        <infinite-loading :slots="{complete: ' ', error: ' '}"
                                          @infinite="infiniteHandler($event, index)">
                            <template #spinner>
                                <div class="p-16">
                                    <loading size="small"></loading>
                                </div>
                            </template>
                        </infinite-loading>
                    </template>
                    <li v-else class="text-primary-80 text-center">
                        {{ $t('applications.no_applicants_found') }}
                    </li>
                </ul>
            </template>
        </div>
    </div>
</template>


<script>

import SvgIcon from '../../components/ui/SvgIcon.vue';
import Tabs from '../../components/navigation/Tabs.vue';
import Loading from '../../components/ui/Loading.vue';
import Avatar from '../../components/ui/Avatar.vue';
import moment from 'moment';
import Tooltip from '../../components/navigation/Tooltip.vue';
import Checkbox from '../../components/forms/Checkbox.vue';
import InfiniteLoading from 'v3-infinite-loading/lib/v3-infinite-loading.es.js';
import * as dashboardService from '../../services/DashboardService';
import {useMainStore} from '../../state/store';
import {ADD_NOTIFICATION} from '../../state/mutation-types';
import {responseErrorMessage} from '../../etc/interceptors';
import {mapState, mapStores} from "pinia";
import {useDashboardStore} from "../../state/modules/dashboard";
import {useNotificationsStore} from "../../state/modules/notifications";
import {useAuthenticationStore} from "../../state/modules/authentication";

export default {
    components: {
        Checkbox,
        Tooltip,
        Avatar,
        Loading,
        Tabs,
        SvgIcon,
        moment,
        'infinite-loading': InfiniteLoading,
    },
    data: function () {
        return {
            activeTab: 'stale',
        };
    },
    computed: {
        ...mapStores(useMainStore, useDashboardStore, useNotificationsStore),
        ...mapState(useAuthenticationStore, ['contentLanguage']),
        locale() {
            return this.mainStore.locale;
        },
        fetching() {
            return this.dashboardStore.fetching;
        },
        staleApplications() {
            return this.dashboardStore.staleApplications;
        },
        newApplications() {
            return this.dashboardStore.newApplications;
        },
        interviewApplications() {
            return this.dashboardStore.interviewApplications;
        },
        favoriteApplications() {
            return this.dashboardStore.favoriteApplications;
        },
        staleApplicationCount() {
            return this.dashboardStore.staleApplicationCount;
        },
        newApplicationCount() {
            return this.dashboardStore.newApplicationCount;
        },
        interviewApplicationCount() {
            return this.dashboardStore.interviewApplicationCount;
        },
        favoriteApplicationCount() {
            return this.dashboardStore.favoriteApplicationCount;
        },
        tabItems() {
            return {
                'stale': {
                    'title': this.$t('general.stale'),
                    'count': this.staleApplicationCount,
                    'badge': 'info',
                    'applications': this.staleApplications,
                },
                'new': {
                    'title': this.$t('general.new'),
                    'count': this.newApplicationCount,
                    'badge': 'info',
                    'applications': this.newApplications,
                },
                'interview': {
                    'title': this.$t('interview.interview'),
                    'count': this.interviewApplicationCount,
                    'badge': 'info',
                    'applications': this.interviewApplications,
                },
                'favorite': {
                    'title': this.$t('general.favorites'),
                    'icon': 'star',
                    'iconColor': 'fill-semantic-warning',
                    'count': this.favoriteApplicationCount,
                    'badge': 'info',
                    'applications': this.favoriteApplications,
                },
            };
        },
    },
    methods: {
        infiniteHandler(state, type) {
            //Todo:: with time split the fetch in different calls, when state is loaded for one fetch the other fetch isn't working anymore since state is 'loaded'
            //Todo:: This would also improve visual reading of the code here and in the controller
            let offset = 0;

            if (type === 'stale') {
                offset = this.staleApplications.length;
            }
            if (type === 'new') {
                offset = this.newApplications.length;
            }
            if (type === 'interview') {
                offset = this.interviewApplications.length;
            }
            if (type === 'favorite') {
                offset = this.favoriteApplications.length;
            }

            return this.fetchApplications(offset, type)
                .then(loadState => {
                    if (loadState) {
                        state.loaded();
                    } else {
                        state.complete();
                    }
                });
        },
        fetchApplications(offset, type) {
            let limit = 10;

            return new Promise((resolve, reject) => {
                let loaded = undefined;

                dashboardService.applications({offset, limit, type})
                    .then(result => {
                        this.dashboardStore.set(result);

                        loaded = !!result[type].length;
                        resolve(loaded);
                    }).catch(error => {
                        this.notificationsModuleStore[ADD_NOTIFICATION]({
                            type: 'error',
                            message: responseErrorMessage(error)
                        });
                    });
            });
        },
        getFullName(item) {
            return (item.first_name ? item.first_name + ' ' : '') + (item.last_name ? item.last_name : '');
        },
        getInitials(item) {
            return (item.first_name ? item.first_name.charAt(0) : '') + (item.last_name ? item.last_name.charAt(0) : '');
        },
        getCreateDate(date) {
            return moment.utc(date).local().locale(this.locale).fromNow(true);
        },
        getInterviewDate(date) {
            return this.$t('notifications.application.interviewConfirmed_subtext', {date: moment.utc(date).local().format('LLL')});
        },
        getTooltipCreateDate(date) {
            return this.$t('auth.api_keys.created_at', {date: moment.utc(date).local().format('LLL')});
        },
        getJobTitle(item) {
            if (item.job_title) {
                let title = this.translated(item.job_title, this.contentLanguage, true);

                if (title) {
                    return title;
                }
            }

            return this.$t('jobs.missing_title');
        },
        getJobLocation(item) {
            return item.location.city;
        }
    },
};
</script>
